import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Shield, CheckCircle2, ArrowRight, ArrowLeft, Check, Users } from 'lucide-react';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import Button from '../components/Button';
import CTASection from '../components/CTASection';
import { servicesData } from '../data/servicesData';

export default function ServiceDetail({ onOpenConsultation }) {
  const { serviceId } = useParams();
  const service = servicesData.find((s) => s.slug === serviceId || s.id === serviceId);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const otherServices = servicesData.filter((s) => s.id !== service.id);

  return (
    <div className="bg-canvas">
      <SEO
        title={`${service.title} | UMED Care Programs`}
        description={service.shortDescription}
      />

      {/* Hero */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 bg-[#123B5D] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#0F766E]/30 via-[#123B5D] to-[#123B5D] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="mb-6">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-xs font-semibold text-[#2A9D8F] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to All Clinical Services</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0F766E] border border-[#2A9D8F]/30">
                <Shield className="w-3.5 h-3.5 text-[#2A9D8F]" />
                <span className="text-xs uppercase tracking-widest text-white font-semibold">
                  {service.tag}
                </span>
              </div>

              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-normal leading-tight">
                {service.title}
              </h1>

              <p className="text-base sm:text-lg text-[#D9E8E5] leading-relaxed max-w-2xl">
                {service.shortDescription}
              </p>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Button onClick={onOpenConsultation} variant="primary" size="lg">
                  {service.ctaText}
                </Button>
                <Button to="/contact" variant="darkOutline" size="lg">
                  Contact Admissions Desk
                </Button>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-elevated border border-[#D9E8E5] aspect-[4/3] bg-[#123B5D]">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview & Who It Supports */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Overview & Clinical Approach */}
            <div className="lg:col-span-7 space-y-10">
              <div>
                <SectionHeading
                  badge="PROGRAM OVERVIEW"
                  title="Clinical Care Strategy"
                  subtitle="A thorough, compassionate, and multidisciplinary treatment methodology."
                />
                <p className="mt-4 text-sm sm:text-base text-[#64748B] leading-relaxed">
                  {service.overview}
                </p>
              </div>

              {/* Step-by-Step Clinical Approach */}
              <div>
                <h3 className="font-serif text-2xl text-[#123B5D] font-normal mb-6">
                  Our Multidisciplinary Clinical Approach
                </h3>
                <div className="space-y-4">
                  {service.approach.map((step, idx) => (
                    <div
                      key={idx}
                      className="p-6 rounded-2xl bg-white border border-[#D9E8E5] shadow-card"
                    >
                      <div className="flex items-start gap-4">
                        <span className="w-8 h-8 rounded-full bg-[#F5FAF9] text-[#0F766E] font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                          0{idx + 1}
                        </span>
                        <div>
                          <h4 className="font-serif text-lg text-[#123B5D] font-normal mb-1">
                            {step.title}
                          </h4>
                          <p className="text-xs sm:text-sm text-[#64748B] leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Conditions, Who It Supports & Key Modules */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Conditions & Care Scope Card */}
              {service.conditions && service.conditions.length > 0 && (
                <div className="p-8 rounded-3xl bg-white border border-[#D9E8E5] shadow-card">
                  <h3 className="font-serif text-xl font-normal text-[#123B5D] mb-3">
                    Conditions & Care Scope
                  </h3>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {service.conditions.map((cond, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-[#F5FAF9] text-[#0F766E] border border-[#D9E8E5]"
                      >
                        {cond}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Who It Supports Card */}
              <div className="p-8 rounded-3xl bg-[#F5FAF9] border border-[#D9E8E5] shadow-card">
                <div className="flex items-center gap-2 mb-4 text-[#123B5D]">
                  <Users className="w-5 h-5 text-[#0F766E]" />
                  <h3 className="font-serif text-xl font-normal text-[#123B5D]">
                    Who This Program Supports
                  </h3>
                </div>
                <div className="space-y-3">
                  {service.whoItSupports.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-[#64748B]">
                      <CheckCircle2 className="w-4 h-4 text-[#0F766E] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Core Treatment Modules Card */}
              <div className="p-8 rounded-3xl bg-[#123B5D] text-white border border-[#0F766E]/30 shadow-card">
                <h3 className="font-serif text-xl font-normal text-white mb-4">
                  Core Treatment Modules
                </h3>
                <div className="space-y-2.5">
                  {service.modules.map((mod, idx) => (
                    <div key={idx} className="p-3 rounded-xl bg-[#0D273E]/90 border border-white/10 text-xs text-[#D9E8E5] flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#2A9D8F] flex-shrink-0" />
                      <span>{mod}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <Button
                    onClick={onOpenConsultation}
                    variant="primary"
                    size="md"
                    className="w-full"
                  >
                    Schedule Assessment
                  </Button>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Explore Other Services */}
      <section className="py-16 bg-[#F5FAF9] border-t border-[#D9E8E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-serif text-2xl text-[#123B5D] font-normal mb-8">
            Explore Other Specialized Programs
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherServices.slice(0, 3).map((item) => (
              <Link
                key={item.id}
                to={`/services/${item.slug}`}
                className="p-6 rounded-2xl bg-white border border-[#D9E8E5] shadow-card hover:shadow-medium transition-all group"
              >
                <span className="text-[11px] font-semibold text-[#0F766E] uppercase tracking-wider block mb-1">
                  {item.tag}
                </span>
                <h4 className="font-serif text-lg text-[#123B5D] font-normal group-hover:text-[#0F766E] transition-colors">
                  {item.title}
                </h4>
                <p className="text-xs text-[#64748B] mt-2 line-clamp-2">
                  {item.shortDescription}
                </p>
                <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#0F766E] group-hover:text-[#123B5D]">
                  <span>Learn more</span>
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection onOpenConsultation={onOpenConsultation} />
    </div>
  );
}
